export function MetaIntegrationPrivacy() {
  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <h1 className="text-3xl font-bold heading">Política de Privacidade - Integração Meta</h1>

      <div className="text-sm text-dim">
        Última atualização: 24 de outubro de 2025
      </div>

      <div className="prose prose-invert max-w-none space-y-6" style={{ color: 'var(--cg-text)' }}>
        <section>
          <h2 className="text-xl font-semibold heading mb-3">1. Introdução</h2>
          <p>
            Esta Política de Privacidade descreve como o CopyGenius coleta, usa, armazena e protege seus dados ao utilizar a integração com Instagram e Facebook (Meta Platforms).
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold heading mb-3">2. Dados Coletados</h2>
          <p><strong>Quando você conecta uma conta Instagram ou Facebook, coletamos:</strong></p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>
              <strong>ID da Conta:</strong> Identificador único fornecido pela Meta (Instagram Business Account ID ou Facebook Page ID)
            </li>
            <li>
              <strong>Nome da Conta:</strong> Nome de exibição público da sua conta ou página
            </li>
            <li>
              <strong>Username:</strong> @nome de usuário (Instagram) ou nome da página (Facebook)
            </li>
            <li>
              <strong>Foto de Perfil:</strong> URL da imagem de perfil pública
            </li>
            <li>
              <strong>Token de Acesso:</strong> Credencial OAuth fornecida pela Meta para realizar publicações
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold heading mb-3">3. Como Usamos Seus Dados</h2>
          <p><strong>Seus dados são usados exclusivamente para:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Autenticar e manter a conexão com sua conta Meta</li>
            <li>Publicar conteúdo (posts) quando você explicitamente solicitar</li>
            <li>Exibir informações da conta conectada (nome, foto) na interface</li>
            <li>Gerenciar o status da conexão</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold heading mb-3">4. Armazenamento e Segurança</h2>
          <div className="space-y-3">
            <div>
              <strong className="block mb-1">Criptografia:</strong>
              <p>
                Todos os tokens de acesso são criptografados usando AES-256 antes de serem armazenados em nosso banco de dados. As chaves de criptografia são armazenadas separadamente e com acesso restrito.
              </p>
            </div>
            <div>
              <strong className="block mb-1">Localização:</strong>
              <p>
                Seus dados são armazenados em servidores seguros localizados no Brasil, em conformidade com a LGPD.
              </p>
            </div>
            <div>
              <strong className="block mb-1">Acesso Restrito:</strong>
              <p>
                Apenas sistemas automatizados e pessoal técnico autorizado têm acesso aos dados, e apenas quando estritamente necessário para manutenção do serviço.
              </p>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-xl font-semibold heading mb-3">5. Compartilhamento de Dados</h2>
          <p className="font-semibold" style={{ color: 'var(--cg-accent)' }}>
            <i className="fa-solid fa-triangle-exclamation mr-1"></i> NÃO compartilhamos seus dados com terceiros.
          </p>
          <p className="mt-2">
            Seus tokens de acesso e informações da conta são usados exclusivamente dentro do CopyGenius para realizar as ações que você solicita. Não vendemos, alugamos ou compartilhamos seus dados com empresas de marketing, anunciantes ou outras entidades.
          </p>
          <p className="mt-2">
            <strong>Exceção:</strong> Podemos divulgar dados se legalmente obrigados por ordem judicial ou autoridade competente.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold heading mb-3">6. Retenção de Dados</h2>
          <p>
            Seus dados são mantidos enquanto você mantém a conta conectada. Ao desconectar uma conta:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>O token de acesso é imediatamente revogado na API da Meta</li>
            <li>Todos os dados relacionados à conexão são permanentemente excluídos de nosso banco</li>
            <li>Não mantemos backups dos tokens após a revogação</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold heading mb-3">7. Relação com a Meta</h2>
          <p>
            O CopyGenius utiliza a Graph API oficial da Meta Platforms para integração. Estamos sujeitos às políticas e termos de uso da Meta. Ao conectar suas contas, você também está sujeito à <a href="https://www.facebook.com/privacy/policy" target="_blank" rel="noopener noreferrer" className="text-accent underline">Política de Privacidade do Facebook</a> e <a href="https://privacycenter.instagram.com/policy" target="_blank" rel="noopener noreferrer" className="text-accent underline">Política de Privacidade do Instagram</a>.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold heading mb-3">8. Seus Direitos (LGPD)</h2>
          <p>
            De acordo com a LGPD, você tem os seguintes direitos sobre seus dados:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li><strong>Acesso:</strong> Solicitar cópia de todos os dados que temos sobre você</li>
            <li><strong>Correção:</strong> Solicitar correção de dados imprecisos</li>
            <li><strong>Exclusão:</strong> Solicitar exclusão de seus dados</li>
            <li><strong>Portabilidade:</strong> Solicitar exportação de seus dados</li>
            <li><strong>Revogação:</strong> Revogar consentimento a qualquer momento</li>
            <li><strong>Oposição:</strong> Opor-se a tratamentos específicos</li>
          </ul>
          <p className="mt-3">
            Para exercer qualquer destes direitos, acesse as Configurações da Conta ou entre em contato pelo e-mail: <a href="mailto:privacidade@copygenius.com" className="text-accent">privacidade@copygenius.com</a>
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold heading mb-3">9. Cookies e Rastreamento</h2>
          <p>
            A integração Meta não utiliza cookies adicionais. O processo de autenticação OAuth ocorre no domínio da Meta (facebook.com) e está sujeito às políticas de cookies da Meta.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold heading mb-3">10. Segurança e Brechas</h2>
          <p>
            Implementamos medidas de segurança técnicas e organizacionais para proteger seus dados. Em caso de brecha de segurança que afete dados sensíveis, você será notificado dentro de 72 horas, conforme exigido pela LGPD.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold heading mb-3">11. Alterações a Esta Política</h2>
          <p>
            Podemos atualizar esta Política de Privacidade periodicamente. Mudanças significativas serão notificadas por e-mail e/ou através de aviso destacado no aplicativo. A data da última atualização sempre estará indicada no topo deste documento.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold heading mb-3">12. Encarregado de Dados (DPO)</h2>
          <p>
            Para questões relacionadas à privacidade e proteção de dados:
          </p>
          <ul className="list-none space-y-1 ml-4">
            <li><strong>E-mail:</strong> <a href="mailto:dpo@copygenius.com" className="text-accent">dpo@copygenius.com</a></li>
            <li><strong>Endereço:</strong> [Seu endereço comercial]</li>
          </ul>
        </section>

        <div className="p-4 rounded-lg border-2 mt-8" style={{
          backgroundColor: 'var(--cg-success-bg)',
          borderColor: 'var(--cg-success)'
        }}>
          <h3 className="font-semibold mb-2"><i className="fa-solid fa-check-circle mr-1"></i> Resumo</h3>
          <ul className="list-disc list-inside space-y-1 ml-2 text-sm">
            <li>Coletamos apenas dados necessários para a publicação</li>
            <li>Tokens são criptografados com AES-256</li>
            <li>NÃO compartilhamos com terceiros</li>
            <li>Você pode revogar acesso a qualquer momento</li>
            <li>Conformidade total com LGPD</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
